/**
 * Token value object - immutable and identified by its properties
 */
export class Token {
  private readonly _value: string
  private readonly _expiresAt: Date | null
  private readonly _type: TokenType

  constructor(value: string, type: TokenType, expiresAt: Date | null = null) {
    this._value = value
    this._type = type
    this._expiresAt = expiresAt
  }

  get value(): string {
    return this._value
  }

  get type(): TokenType {
    return this._type
  }

  get expiresAt(): Date | null {
    return this._expiresAt
  }

  /**
   * Checks if token is expired
   */
  isExpired(): boolean {
    if (!this._expiresAt) {
      return false
    }

    return new Date() > this._expiresAt
  }

  /**
   * Returns remaining time in seconds
   */
  getRemainingTime(): number | null {
    if (!this._expiresAt) {
      return null
    }

    const remaining = this._expiresAt.getTime() - Date.now()
    return Math.max(0, Math.floor(remaining / 1000))
  }

  /**
   * Creates a new token with same type and new expiration date
   */
  withNewExpiration(expiresAt: Date): Token {
    return new Token(this._value, this._type, expiresAt)
  }

  /**
   * Creates access token
   */
  static createAccessToken(value: string, expiresIn: number): Token {
    const expiresAt = new Date()
    expiresAt.setSeconds(expiresAt.getSeconds() + expiresIn)

    return new Token(value, 'access', expiresAt)
  }

  /**
   * Creates refresh token
   */
  static createRefreshToken(value: string, expiresIn: number): Token {
    const expiresAt = new Date()
    expiresAt.setSeconds(expiresAt.getSeconds() + expiresIn)

    return new Token(value, 'refresh', expiresAt)
  }
}

/**
 * Types of authentication tokens
 */
export type TokenType = 'access' | 'refresh'
